import { getStatus, OperationOutcomeError } from '@medplum/core'
import type {
  Coding,
  Parameters,
  ParametersParameter,
} from '@medplum/fhirtypes'
import { match, P } from 'ts-pattern'
import type { SeenMedplumClient } from '#src/client.ts'
import { LOINC_CODE_SYSTEM } from '#src/codeSystems.ts'
import {
  type CodingAnswerOption,
  type CodingDetails,
  isScaleType,
  type ObservationValueKind,
  type ScaleType,
} from '#src/codings.ts'
import { VITAL_OPTIONS } from '#src/models/loinc.ts'
import { libraryCodingDetails } from '#src/terminology/library.ts'
import {
  observationTemplate,
  templateDetails,
} from '#src/terminology/observationTemplates.ts'

// Longer answer lists are searched in their value set rather than carried on
// every node that records the code.
const ANSWER_LIST_LIMIT = 100

// How a code is captured: its scale, unit and answers. Built-in vitals and
// our own question codes answer from source; every other code is looked up
// on the terminology server.
export async function getCodingDetails(
  medplum: SeenMedplumClient,
  coding: Coding
): Promise<CodingDetails> {
  const { code, system } = coding
  if (!code || !system) {
    return {
      scaleType: 'Qn',
      unit: null,
      answerOptions: [],
      display: '',
    }
  }

  const fromSource =
    builtInCodingDetails(coding) ?? libraryCodingDetails(coding)
  if (fromSource) {
    return fromSource
  }

  let scaleType: ScaleType = 'Qn'
  let unit: string | null = null
  let valueKind: ObservationValueKind | undefined
  let isPanel = false
  let loincClass: string | undefined
  let loincClassType: string | undefined
  const lookupAnswers: CodingAnswerOption[] = []
  const answerLists: string[] = []

  const codeSystem = await medplum.get<Parameters>(
    `/fhir/R4/CodeSystem/$lookup?system=${encodeURIComponent(system)}&code=${encodeURIComponent(code)}`
  )

  const display =
    codeSystem?.parameter?.find((p) => p.name === 'display')?.valueString ?? ''

  if (codeSystem?.parameter) {
    const properties = codeSystem.parameter.filter((p) => p.name === 'property')

    for (const prop of properties) {
      const propCode = prop.part?.find((p) => p.name === 'code')?.valueCode
      const propValue = propertyValue(prop)

      // A scale the node cannot record (a panel's `-`) keeps the number
      // field it has always fallen back to.
      if (propCode === 'SCALE_TYP' && propValue && isScaleType(propValue)) {
        scaleType = propValue
      }

      if (propCode === 'CLASS' && propValue) {
        loincClass = propValue
      }
      if (propCode === 'CLASSTYPE' && propValue) {
        loincClassType = propValue
      }

      if (
        (propCode === 'PanelType' && propValue === 'Panel') ||
        (propCode === 'CLASS' && propValue?.startsWith('PANEL.'))
      ) {
        isPanel = true
      }

      // Most accurate unit source: EXAMPLE_UCUM_UNITS
      if (propCode === 'EXAMPLE_UCUM_UNITS' && propValue) {
        unit = overrideExampleUnitMapping(propValue)
      }

      if (propCode === 'PROPERTY' && propValue) {
        valueKind = valueKindFromProperty(propValue)
        // Optional fallback inference if EXAMPLE_UCUM_UNITS is missing
        unit ||= inferUnitFromProperty(propValue)
      }

      if (propCode === 'answer-list' && propValue) {
        answerLists.push(answerListUrl(system, propValue))
      }

      if (propCode === 'answer_to') {
        const answerDisplay = prop.part?.find(
          (p) => p.name === 'description'
        )?.valueString
        if (propValue && answerDisplay) {
          lookupAnswers.push({ code: propValue, display: answerDisplay })
        }
      }
    }
  }

  // A code can name more than one answer list (LOINC pain severity names
  // two). The first list the server holds is used whole; `answer_to` merges
  // every list into one, so it is only the fallback.
  for (const url of answerLists) {
    const expanded = await expandAnswerList(medplum, url)
    if (expanded.length > ANSWER_LIST_LIMIT) {
      return {
        scaleType,
        unit,
        answerOptions: [],
        answerValueSet: url,
        display,
      }
    }
    if (expanded.length > 0) {
      return { scaleType, unit, answerOptions: expanded, display }
    }
  }

  const template = observationTemplate(coding)
  if (template) {
    const details: CodingDetails = { ...templateDetails(template), display }
    return isPanel
      ? { ...details, ...panelDetails(loincClass, loincClassType) }
      : details
  }

  if (scaleType === 'Qn' && valueKind) {
    return { scaleType, unit: null, valueKind, answerOptions: [], display }
  }

  // LOINC answer lists are often not imported, and the node then offers its
  // default answers as it always has. One of our own codes naming a list
  // that is gone is a mistake in the library, and guessing answers for it
  // would put wrong values on the chart.
  if (
    answerLists.length > 0 &&
    lookupAnswers.length === 0 &&
    system !== LOINC_CODE_SYSTEM
  ) {
    throw new Error(
      `The answer list for "${display || code}" could not be found, so it cannot be recorded here.`
    )
  }

  const details: CodingDetails = {
    scaleType,
    unit,
    answerOptions: lookupAnswers,
    display,
  }
  return isPanel
    ? { ...details, ...panelDetails(loincClass, loincClassType) }
    : details
}

function builtInCodingDetails(coding: Coding): CodingDetails | undefined {
  for (const vital of VITAL_OPTIONS) {
    const vitalCoding = vital.coding.coding?.find(
      (candidate) =>
        candidate.system === coding.system && candidate.code === coding.code
    )
    if (vitalCoding) {
      const template = observationTemplate(vitalCoding)
      const details: CodingDetails = {
        scaleType: vital.scaleType,
        unit: vital.unit,
        answerOptions: vital.answerOptions ?? [],
        display: vitalCoding.display ?? vital.display,
      }
      return template ? { ...details, ...templateDetails(template) } : details
    }
  }
  return undefined
}

function propertyValue(property: ParametersParameter): string | undefined {
  const value = property.part?.find((part) => part.name === 'value')
  return value?.valueString ?? value?.valueCode
}

// LOINC names an answer list by its id; our own code systems name the value
// set by its url.
function answerListUrl(system: string, value: string): string {
  return system === LOINC_CODE_SYSTEM && !value.includes(':')
    ? `http://loinc.org/vs/${value}`
    : value
}

async function expandAnswerList(
  medplum: SeenMedplumClient,
  url: string
): Promise<CodingAnswerOption[]> {
  try {
    // One more than fits on the node, to tell a long list from a full one.
    const valueSet = await medplum.valueSetExpand({
      url,
      count: ANSWER_LIST_LIMIT + 1,
    })
    return (valueSet.expansion?.contains ?? []).flatMap((concept) => {
      if (!concept.code) {
        return []
      }
      const option: CodingAnswerOption = {
        code: concept.code,
        display: concept.display ?? concept.code,
      }
      if (concept.system) {
        option.system = concept.system
      }
      return [option]
    })
  } catch (error) {
    // Medplum answers 400 for a value set it does not hold, which is the
    // case for LOINC answer lists that were never imported.
    if (
      error instanceof OperationOutcomeError &&
      [400, 404].includes(getStatus(error.outcome))
    ) {
      return []
    }
    throw error
  }
}

// Unified Code for Units of Measure (UCUM) https://ucum.org/ucum
function overrideExampleUnitMapping(unit: string): string {
  return (
    match(unit)
      .with('Cel', () => '[degF]')
      .with(
        P.union(P.string.includes('in_us'), P.string.includes('lb_av')),
        () => (unit.includes('in_us') ? '[in_us]' : '[lb_av]')
      )
      // Example units may show multiple units delimited by a semicolon. In these cases, we only want to show the first unit.
      .with(P.string.includes(';'), () => unit.split(';')[0])
      .otherwise(() => unit)
  )
}

// LOINC's PROPERTY for a value that is a point in time. `Time` is not one:
// it is a duration, measured in a unit.
function valueKindFromProperty(
  property: string
): ObservationValueKind | undefined {
  return match(property)
    .with('Date', () => 'date' as const)
    .with('TmStp', () => 'dateTime' as const)
    .otherwise(() => undefined)
}

function inferUnitFromProperty(property: string): string | null {
  interface UnitsByProperty {
    [property: string]: string
  }

  const unitMap: UnitsByProperty = {
    MCnc: 'mg/dL',
    SCnc: 'mmol/L',
    Temp: '°F',
    Pres: 'mmHg',
    Len: 'inches',
    Mass: 'lbs',
    Vol: 'mL',
    Time: 's',
    Freq: 'Hz',
    Vel: 'm/s',
    Accel: 'm/s²',
    Force: 'N',
    Energy: 'J',
    Power: 'W',
  }

  for (const [key, unit] of Object.entries(unitMap)) {
    if (property.includes(key)) {
      return unit
    }
  }

  return null
}

function panelDetails(
  loincClass: string | undefined,
  loincClassType: string | undefined
): Pick<CodingDetails, 'isPanel' | 'loincClass' | 'loincClassType'> {
  const details: Pick<
    CodingDetails,
    'isPanel' | 'loincClass' | 'loincClassType'
  > = { isPanel: true }
  if (loincClass) {
    details.loincClass = loincClass
  }
  if (loincClassType) {
    details.loincClassType = loincClassType
  }
  return details
}
